/**
 * Msg 562 · D.4 — Global jump-to-topic search.
 *
 * Persistent input in the Atlas top nav + prominent variant on the
 * /atlas/topics landing page. Debounced fetch to
 *   GET /api/atlas/topics/search?q=...&limit=20
 * with keyboard nav (↑↓ Enter Esc).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ChevronRight, Loader2, X as CloseIcon } from "lucide-react";
import { api } from "@/lib/api";

import { Chip, EntityIcon, humanize } from "./AtlasTopicShared";


function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}


export function TopicSearchBar({
  size = "md",           // "md" | "lg" (lg = landing variant)
  testId = "atlas-topic-search",
  autoFocus = false,
  placeholder = "Jump to a topic…",
}) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const debounced = useDebounced(q, 200);

  // Fetch on debounced query.
  useEffect(() => {
    const qn = (debounced || "").trim();
    if (qn.length < 2) {
      setResults([]); setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api.get(`/atlas/topics/search?q=${encodeURIComponent(qn)}&limit=20`)
      .then((r) => {
        if (cancelled) return;
        setResults(r.data?.results || []);
        setActive(0);
      })
      .catch(() => { if (!cancelled) setResults([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debounced]);

  // Click outside → close dropdown.
  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const goTo = (r) => {
    if (!r) return;
    setOpen(false);
    setQ("");
    setResults([]);
    nav(`/atlas/topics/${r.slug}`);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault(); setOpen(true);
      setActive((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      if (results[active]) { e.preventDefault(); goTo(results[active]); }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const inputHeight = size === "lg" ? "h-12" : "h-9";
  const inputPad    = size === "lg" ? "pl-12 pr-10 text-base" : "pl-9 pr-8 text-sm";
  const iconLeft    = size === "lg" ? "left-4" : "left-2.5";
  const iconRight   = size === "lg" ? "right-3" : "right-2";
  const showDropdown = open && (debounced || "").trim().length >= 2;

  return (
    <div
      ref={wrapRef}
      className={size === "lg" ? "relative w-full" : "relative w-full max-w-md"}
      data-testid={testId}
    >
      <Search
        className={`absolute ${iconLeft} top-1/2 -translate-y-1/2 ${
          size === "lg" ? "w-4 h-4" : "w-3.5 h-3.5"} text-tb-navy/40 pointer-events-none`}
        aria-hidden="true"
      />
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        autoFocus={autoFocus}
        placeholder={placeholder}
        data-testid={`${testId}-input`}
        className={`w-full ${inputHeight} ${inputPad} rounded-full border border-tb-navy/20 bg-white outline-none focus:border-tb-navy font-hand text-tb-navy placeholder:text-tb-navy/40`}
      />
      {q && (
        <button
          type="button"
          onClick={() => { setQ(""); setResults([]); inputRef.current?.focus(); }}
          data-testid={`${testId}-clear`}
          className={`absolute ${iconRight} top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center rounded-full text-tb-navy/40 hover:bg-tb-navy/5`}
          aria-label="Clear search"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CloseIcon className="w-3.5 h-3.5" />}
        </button>
      )}

      {showDropdown && (
        <ResultsDropdown
          results={results}
          loading={loading}
          active={active}
          setActive={setActive}
          onPick={goTo}
        />
      )}
    </div>
  );
}


function ResultsDropdown({ results, loading, active, setActive, onPick }) {
  return (
    <div
      className="absolute left-0 right-0 top-full mt-1 z-40 rounded-2xl border border-tb-navy/15 bg-white shadow-xl max-h-[420px] overflow-y-auto"
      data-testid="atlas-topic-search-dropdown"
      role="listbox"
    >
      {loading && results.length === 0 && (
        <div className="px-4 py-3 font-hand text-xs text-tb-navy/50">Searching…</div>
      )}
      {!loading && results.length === 0 && (
        <div className="px-4 py-3 font-hand text-sm text-tb-navy/50" data-testid="atlas-topic-search-empty">
          No matching topics.
        </div>
      )}
      {results.map((r, i) => (
        <ResultRow
          key={r.slug}
          r={r}
          active={i === active}
          onHover={() => setActive(i)}
          onPick={() => onPick(r)}
        />
      ))}
    </div>
  );
}


function ResultRow({ r, active, onHover, onPick }) {
  const ancestorText = (r.ancestor_path || []).map((a) => a.name).join(" / ");
  return (
    <button
      type="button"
      onMouseEnter={onHover}
      onClick={onPick}
      data-testid={`atlas-topic-search-result-${r.slug}`}
      aria-selected={active}
      className={`w-full text-left px-4 py-2.5 border-b border-tb-navy/5 last:border-b-0 transition ${
        active ? "bg-tb-navy/5" : "hover:bg-tb-navy/[0.03]"
      }`}
    >
      <div className="flex items-center gap-2">
        <EntityIcon categoryId={r.category_id} className="w-3.5 h-3.5 text-tb-navy/60 shrink-0" />
        <span className="font-hand text-sm text-tb-navy font-semibold truncate">{r.name}</span>
        {r.category && (
          <Chip>{humanize(r.category)}</Chip>
        )}
      </div>
      {ancestorText && (
        <div className="mt-1 flex items-center gap-1 text-tb-navy/50 font-hand text-[11px] ml-6 truncate"
             data-testid={`atlas-topic-search-ancestors-${r.slug}`}>
          <span className="truncate">{ancestorText}</span>
          <ChevronRight className="w-3 h-3 shrink-0 text-tb-navy/40" />
          <span className="truncate text-tb-navy/70">{r.name}</span>
        </div>
      )}
    </button>
  );
}
